import { useCallback, useRef, useState } from 'react';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { fetchLinkingCandidateDetails, type SearchParams } from './api';
import type { ProfileLinkingSearchResponse, UiProfile } from './types';
import {
  batchGetProfilesByIds,
  buildUiCandidate,
  getProfileByHandle,
  getProfileById,
  mapProfileToUiProfile,
  selectEffectiveEvaluation,
  UpsDirectAuthFailedError,
  UpsDirectNetworkError,
} from './directUps';

// The single shared search/orchestration implementation used by both the
// standalone /profile-linking page and the embedded Unified Profiles
// section: direct browser -> UPS Profile API for lookup/hydration, plus the
// tokenless Backstage backend endpoint for linking-storage candidates.
export type ProfileLinkingExplorerState =
  | { status: 'initial' }
  | { status: 'loading' }
  | { status: 'not-found'; message: string }
  | { status: 'ups-auth-failed' }
  | { status: 'ups-network-error' }
  | { status: 'storage-error'; message: string }
  | { status: 'error'; message: string }
  | { status: 'success'; data: ProfileLinkingSearchResponse };

export function useProfileLinkingExplorer() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<ProfileLinkingExplorerState>({ status: 'initial' });
  const lastArgs = useRef<{ params: SearchParams; token: string } | undefined>(undefined);

  const run = useCallback(
    async (params: SearchParams, token: string) => {
      lastArgs.current = { params, token };
      setState({ status: 'loading' });
      try {
        // 1. Source profile — direct browser -> UPS.
        const unifiedProfile = params.handle
          ? await getProfileByHandle(params.network, params.handle, token)
          : await getProfileById(params.network, params.profileId as string, token);

        const sourceProfile = mapProfileToUiProfile(unifiedProfile.profile, params.network);
        if (!sourceProfile || !sourceProfile.nativeProfileId) {
          setState({
            status: 'not-found',
            message: `No ${params.network} identity with a native profile id was found for this search.`,
          });
          return;
        }

        // 2. Candidates + evaluations — tokenless, via Backstage backend.
        let details;
        try {
          details = await fetchLinkingCandidateDetails(
            discoveryApi,
            fetchApi,
            params.network,
            sourceProfile.nativeProfileId,
          );
        } catch {
          setState({ status: 'storage-error', message: 'Unable to load linking candidates.' });
          return;
        }

        // 3. Target profile hydration — direct browser -> UPS, grouped by network.
        const idsByNetwork = new Map<string, Set<string>>();
        for (const { candidate } of details) {
          if (!candidate.targetProfileId) continue;
          const set = idsByNetwork.get(candidate.targetNetwork) ?? new Set<string>();
          set.add(candidate.targetProfileId);
          idsByNetwork.set(candidate.targetNetwork, set);
        }

        const profileByNetworkAndId = new Map<string, UiProfile>();
        for (const [network, ids] of idsByNetwork.entries()) {
          const batch = await batchGetProfilesByIds(network, [...ids], token);
          for (const [id, up] of Object.entries(batch.ids)) {
            const uiProfile = mapProfileToUiProfile(up.profile, network);
            if (uiProfile) profileByNetworkAndId.set(`${network}:${id}`, uiProfile);
          }
        }

        const candidates = details.map(({ candidate, evaluations }) => {
          const evaluation = selectEffectiveEvaluation(candidate, evaluations);
          const targetProfile = candidate.targetProfileId
            ? profileByNetworkAndId.get(`${candidate.targetNetwork}:${candidate.targetProfileId}`) ?? null
            : null;
          return buildUiCandidate(candidate, evaluation, targetProfile);
        });

        setState({ status: 'success', data: { sourceProfile, candidates } });
      } catch (err) {
        if (err instanceof UpsDirectAuthFailedError) {
          setState({ status: 'ups-auth-failed' });
        } else if (err instanceof UpsDirectNetworkError) {
          setState({ status: 'ups-network-error' });
        } else {
          setState({ status: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      }
    },
    [discoveryApi, fetchApi],
  );

  const retry = useCallback(() => {
    if (lastArgs.current) void run(lastArgs.current.params, lastArgs.current.token);
  }, [run]);

  return { state, search: run, retry };
}
